import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../Toast';
import ConfirmationModal from '../ConfirmationModal';
import OrganizationSubscription from './OrganizationSubscription';
import {
  MapPinIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import api from '../../services/api';
import authService from '../../services/auth';

const OrganizationDetail = () => {
  const [organization, setOrganization] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [activeTab, setActiveTab] = useState('organization');
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isLoadingSubscription, setIsLoadingSubscription] = useState(false);
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [radius, setRadius] = useState('100');
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [locationSuccess, setLocationSuccess] = useState('');
  const navigate = useNavigate();
  const { showToast } = useToast();

  // Get current user info
  const currentUser = authService.getUser();

  const fetchOrganizationDetails = useCallback(async () => {
    try {
      setIsLoading(true);
      // Use existing endpoint with user's organization_id
      const response = await api.get(`/api/organizations/${currentUser.organization_id}`);
      setOrganization(response.data);
      setEditForm({
        name: response.data.name,
        address: response.data.address,
        website: response.data.website
      });
      // Pre-fill location settings
      if (response.data.attendance_location_latitude) {
        setLatitude(response.data.attendance_location_latitude.toString());
      }
      if (response.data.attendance_location_longitude) {
        setLongitude(response.data.attendance_location_longitude.toString());
      }
      if (response.data.attendance_location_radius) {
        setRadius(response.data.attendance_location_radius.toString());
      }
    } catch (error) {
      if (error.response?.status === 403) {
        showToast('Access denied to organization details', 'error');
        navigate('/dashboard');
      } else {
        showToast('Failed to fetch organization details', 'error');
      }
    } finally {
      setIsLoading(false);
    }
  }, [navigate, showToast, currentUser.organization_id]);

  const fetchSubscriptionDetails = useCallback(async () => {
    try {
      setIsLoadingSubscription(true);
      const response = await api.get('/api/subscriptions/status');
      setSubscriptionData(response.data);
    } catch (error) {
      console.error('Failed to fetch subscription details:', error);
      showToast('Failed to fetch subscription details', 'error');
    } finally {
      setIsLoadingSubscription(false);
    }
  }, [showToast]);

  useEffect(() => {
    fetchOrganizationDetails();
  }, [fetchOrganizationDetails]);

  // Update form fields when organization data changes
  useEffect(() => {
    if (organization) {
      if (organization.attendance_location_latitude) {
        setLatitude(organization.attendance_location_latitude.toString());
      } else {
        setLatitude('');
      }
      if (organization.attendance_location_longitude) {
        setLongitude(organization.attendance_location_longitude.toString());
      } else {
        setLongitude('');
      }
      if (organization.attendance_location_radius) {
        setRadius(organization.attendance_location_radius.toString());
      } else {
        setRadius('100');
      }
    }
  }, [organization]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditForm({
      name: organization.name,
      address: organization.address,
      website: organization.website
    });
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const response = await api.put(`/api/organizations/${organization.organization_id}`, editForm);
      setOrganization(response.data);
      setIsEditing(false);
      showToast('Organization updated successfully', 'success');
    } catch (error) {
      showToast('Failed to update organization', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleDeleteClick = () => {
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await api.delete(`/api/organizations/${organization.organization_id}`);
      showToast('Organization deleted successfully', 'success');
      // Redirect to login since the user will be deleted too
      setTimeout(() => {
        authService.logout();
      }, 1500);
    } catch (error) {
      showToast('Failed to delete organization', 'error');
      throw error;
    }
  };

  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    if (tabId === 'subscription' && !subscriptionData) {
      fetchSubscriptionDetails();
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-white mb-4">Organization Not Found</h2>
          <p className="text-gray-400 mb-4">Unable to load organization details.</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'organization', name: 'Details', icon: '🏢' },
    { id: 'settings', name: 'Settings', icon: '⚙️' },
    { id: 'subscription', name: 'Subscription', icon: '💳' },
  ];

  const handleDetectLocation = () => {
    setIsDetectingLocation(true);
    setLocationError('');

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude.toString());
          setLongitude(position.coords.longitude.toString());
          setIsDetectingLocation(false);
          setLocationSuccess('Current location detected successfully!');
          setTimeout(() => setLocationSuccess(''), 3000);
        },
        (error) => {
          setIsDetectingLocation(false);
          setLocationError('Unable to detect location. Please enable location services or enter coordinates manually.');
        },
        {
          enableHighAccuracy: true,
          timeout: 10000
        }
      );
    } else {
      setIsDetectingLocation(false);
      setLocationError('Geolocation is not supported by your browser.');
    }
  };

  const handleSaveLocationSettings = async (e) => {
    e.preventDefault();
    setLocationError('');
    setLocationSuccess('');

    // Validate inputs
    const lat = parseFloat(latitude);
    const lon = parseFloat(longitude);
    const rad = parseFloat(radius);

    if (isNaN(lat) || lat < -90 || lat > 90) {
      setLocationError('Latitude must be between -90 and 90');
      return;
    }

    if (isNaN(lon) || lon < -180 || lon > 180) {
      setLocationError('Longitude must be between -180 and 180');
      return;
    }

    if (isNaN(rad) || rad < 10 || rad > 1000) {
      setLocationError('Radius must be between 10 and 1000 meters');
      return;
    }

    try {
      setIsSaving(true);
      const response = await api.put(`/api/organizations/${organization.organization_id}`, {
        attendance_location_latitude: lat,
        attendance_location_longitude: lon,
        attendance_location_radius: rad
      });
      setOrganization(response.data);
      // Update state variables from response
      if (response.data.attendance_location_latitude) {
        setLatitude(response.data.attendance_location_latitude.toString());
      }
      if (response.data.attendance_location_longitude) {
        setLongitude(response.data.attendance_location_longitude.toString());
      }
      if (response.data.attendance_location_radius) {
        setRadius(response.data.attendance_location_radius.toString());
      }
      setLocationSuccess('Location settings updated successfully!');
      showToast('Location settings updated successfully', 'success');
      setTimeout(() => setLocationSuccess(''), 5000);
    } catch (error) {
      setLocationError(error.response?.data?.detail || 'Failed to update location settings');
      showToast('Failed to update location settings', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDisableLocation = async () => {
    if (window.confirm('Are you sure you want to disable location-based attendance? Employees and students will be able to mark attendance from anywhere.')) {
      try {
        setIsSaving(true);
        const response = await api.put(`/api/organizations/${organization.organization_id}`, {
          attendance_location_latitude: null,
          attendance_location_longitude: null,
          attendance_location_radius: null
        });
        setOrganization(response.data);
        setLatitude('');
        setLongitude('');
        setRadius('100');
        showToast('Location-based attendance disabled', 'success');
      } catch (error) {
        showToast('Failed to disable location settings', 'error');
      } finally {
        setIsSaving(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 py-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-white">Organization Details</h1>
              <p className="mt-1 text-gray-400">Manage your organization's information and settings</p>
            </div>
            <div className="flex space-x-3">
              {!isEditing && activeTab === 'organization' ? (
                <button
                  onClick={handleEdit}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
                >
                  Edit Organization
                </button>
              ) : activeTab === 'organization' && isEditing ? (
                <>
                  <button
                    onClick={handleCancel}
                    className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 transition-colors shadow-lg hover:shadow-xl"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors shadow-lg hover:shadow-xl disabled:opacity-50"
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </button>
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-700">
            <nav className="-mb-px flex space-x-8">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`${
                    activeTab === tab.id
                      ? 'border-blue-500 text-blue-400'
                      : 'border-transparent text-gray-400 hover:text-gray-300 hover:border-gray-300'
                  } whitespace-nowrap py-2 px-1 border-b-2 font-medium text-sm transition-colors duration-200`}
                >
                  <span className="mr-2">{tab.icon}</span>
                  {tab.name}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'organization' && (
          <>
            {/* Organization Information */}
            <div className="bg-gray-800 rounded-xl shadow-2xl p-6 border border-gray-700">
              <h2 className="text-lg font-semibold text-white mb-6 flex items-center">
                <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
                Organization Information
              </h2>
              
              <div className="space-y-6">
                {/* Organization ID (Read-only) */}
                <div className="bg-gray-700 p-3 rounded-lg">
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Organization ID
                  </label>
                  <p className="text-blue-400 font-mono text-base font-semibold">{organization.organization_id}</p>
                </div>

                {/* Organization Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Organization Name
                  </label>
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={editForm.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 transition-all"
                      placeholder="Enter organization name"
                    />
                  ) : (
                    <p className="text-white text-lg font-semibold">{organization.name}</p>
                  )}
                </div>

                {/* Address */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Address
                  </label>
                  {isEditing ? (
                    <textarea
                      name="address"
                      value={editForm.address || ''}
                      onChange={handleInputChange}
                      rows={3}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 transition-all resize-none"
                      placeholder="Enter organization address"
                    />
                  ) : (
                    <p className="text-gray-300 text-base">
                      {organization.address || (
                        <span className="text-gray-500 italic">No address provided</span>
                      )}
                    </p>
                  )}
                </div>

                {/* Website */}
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">
                    Website
                  </label>
                  {isEditing ? (
                    <input
                      type="url"
                      name="website"
                      value={editForm.website || ''}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-white placeholder-gray-400 transition-all"
                      placeholder="https://example.com"
                    />
                  ) : (
                    <div className="text-base">
                      {organization.website ? (
                        <a 
                          href={organization.website} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 underline transition-colors"
                        >
                          {organization.website}
                        </a>
                      ) : (
                        <span className="text-gray-500 italic">No website provided</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Danger Zone */}
            {currentUser?.role === 'admin' && (
              <div className="mt-6 bg-red-50/10 rounded-xl shadow-2xl p-6 border border-red-200/20">
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <svg className="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.93 1.75a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm10.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                    </svg>
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-white mb-3">Delete Organization</h3>
                    <p className="text-gray-300 mb-3">
                      This action will permanently delete your organization and <strong>ALL associated data</strong> including:
                    </p>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      <div className="text-gray-400 text-sm">• All students and records</div>
                      <div className="text-gray-400 text-sm">• All batches and enrollments</div>
                      <div className="text-gray-400 text-sm">• All workshops and attendance</div>
                      <div className="text-gray-400 text-sm">• All fees and payments</div>
                      <div className="text-gray-400 text-sm">• All users and accounts</div>
                      <div className="text-gray-400 text-sm">• All tasks and activities</div>
                    </div>
                    <div className="flex items-center space-x-3 mb-4 p-3 bg-red-500/10 rounded-lg border border-red-500/20">
                      <svg className="w-4 h-4 text-red-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.93 1.75a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm10.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                      </svg>
                      <span className="text-red-200 text-sm font-medium">
                        ⚠️ This action cannot be undone. Please ensure you have backed up any important data.
                      </span>
                    </div>
                    <button
                      onClick={handleDeleteClick}
                      className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl font-medium flex items-center space-x-2 hover:scale-105"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      <span>Delete Organization</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'subscription' && (
          <div className="bg-gray-800 rounded-xl shadow-2xl p-6 border border-gray-700">
            <h2 className="text-lg font-semibold text-white mb-6 flex items-center">
              <svg className="w-5 h-5 mr-2 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
              </svg>
              Subscription Details
            </h2>
            
            {isLoadingSubscription ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
                <span className="ml-3 text-gray-400">Loading subscription details...</span>
              </div>
            ) : subscriptionData ? (
              <OrganizationSubscription organization={organization} subscriptionData={subscriptionData} />
            ) : (
              <div className="text-center py-12">
                <div className="text-gray-400 mb-4">
                  <svg className="w-16 h-16 mx-auto mb-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-lg font-medium">No subscription data available</p>
                  <p className="text-sm">Click the refresh button to try again</p>
                </div>
                <button
                  onClick={fetchSubscriptionDetails}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  Refresh Subscription Data
                </button>
              </div>
            )}
          </div>
        )}

        {activeTab === 'settings' && (
          <div className="space-y-6">
            {/* Info Alert */}
            <div className="bg-blue-900/20 border border-blue-700 rounded-lg p-4">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-blue-400 mt-0.5 mr-3 flex-shrink-0" />
                <div>
                  <h3 className="text-sm font-medium text-blue-300">About Location-Based Attendance</h3>
                  <p className="text-sm text-gray-300 mt-1">
                    When configured, employees and students will only be able to mark attendance when they are physically
                    present at your institute within the specified radius. This helps prevent proxy attendance and ensures
                    accurate tracking.
                  </p>
                </div>
              </div>
            </div>

            {/* Status Card */}
            {organization?.attendance_location_latitude && organization?.attendance_location_longitude && (
              <div className="bg-green-900/20 border border-green-700 rounded-lg p-4">
                <div className="flex items-start">
                  <CheckCircleIcon className="h-5 w-5 text-green-400 mt-0.5 mr-3" />
                  <div className="flex-1">
                    <h3 className="text-sm font-medium text-green-300">Location-Based Attendance Active</h3>
                    <p className="text-sm text-gray-300 mt-1">
                      Attendance can only be marked within {organization.attendance_location_radius}m of the configured location.
                    </p>
                    <div className="mt-2 text-xs text-gray-400">
                      <p>Latitude: {organization.attendance_location_latitude}</p>
                      <p>Longitude: {organization.attendance_location_longitude}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Settings Form */}
            <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
              <div className="flex items-center mb-6">
                <MapPinIcon className="h-6 w-6 text-blue-500 mr-2" />
                <h2 className="text-xl font-semibold text-white">Attendance Location Settings</h2>
              </div>

              {locationError && (
                <div className="mb-6 bg-red-900/20 border border-red-700 rounded-lg p-4">
                  <div className="flex">
                    <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
                    <p className="text-red-300 text-sm">{locationError}</p>
                  </div>
                </div>
              )}

              {locationSuccess && (
                <div className="mb-6 bg-green-900/20 border border-green-700 rounded-lg p-4">
                  <div className="flex">
                    <CheckCircleIcon className="h-5 w-5 text-green-400 mr-2" />
                    <p className="text-green-300 text-sm">{locationSuccess}</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSaveLocationSettings} className="space-y-6">
                {/* Detect Current Location Button */}
                <div>
                  <button
                    type="button"
                    onClick={handleDetectLocation}
                    disabled={isDetectingLocation}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <MapPinIcon className="h-5 w-5 mr-2" />
                    {isDetectingLocation ? 'Detecting Location...' : 'Use Current Location'}
                  </button>
                  <p className="text-xs text-gray-400 mt-2">
                    Click to automatically detect and use your current location as the institute location
                  </p>
                </div>

                <div className="border-t border-gray-700 pt-6">
                  <p className="text-sm text-gray-400 mb-4">Or enter coordinates manually:</p>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Latitude */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Latitude <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={latitude}
                        onChange={(e) => setLatitude(e.target.value)}
                        placeholder="e.g., 28.6139"
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <p className="text-xs text-gray-400 mt-1">Range: -90 to 90</p>
                    </div>

                    {/* Longitude */}
                    <div>
                      <label className="block text-sm font-medium text-gray-300 mb-2">
                        Longitude <span className="text-red-400">*</span>
                      </label>
                      <input
                        type="number"
                        step="any"
                        value={longitude}
                        onChange={(e) => setLongitude(e.target.value)}
                        placeholder="e.g., 77.2090"
                        className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                        required
                      />
                      <p className="text-xs text-gray-400 mt-1">Range: -180 to 180</p>
                    </div>
                  </div>

                  {/* Radius */}
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-300 mb-2">
                      Allowed Radius (meters) <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="number"
                      value={radius}
                      onChange={(e) => setRadius(e.target.value)}
                      placeholder="100"
                      min="10"
                      max="1000"
                      className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      required
                    />
                    <p className="text-xs text-gray-400 mt-1">
                      Employees/students must be within this radius to mark attendance (10-1000 meters)
                    </p>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-4">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="flex-1 bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? 'Saving...' : 'Save Location Settings'}
                  </button>

                  {(organization?.attendance_location_latitude && organization?.attendance_location_longitude) && (
                    <button
                      type="button"
                      onClick={handleDisableLocation}
                      disabled={isSaving}
                      className="sm:w-auto bg-red-600 hover:bg-red-700 text-white font-medium py-3 px-6 rounded-lg transition-colors disabled:opacity-50"
                    >
                      Disable Location
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* Help Section */}
            <div className="bg-gray-800 rounded-lg shadow-lg border border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-white mb-4">How to Find Your Coordinates</h3>
              <div className="space-y-3 text-sm text-gray-300">
                <div className="flex items-start">
                  <span className="font-medium text-blue-400 mr-2">1.</span>
                  <p>Click "Use Current Location" button if you're currently at your institute</p>
                </div>
                <div className="flex items-start">
                  <span className="font-medium text-blue-400 mr-2">2.</span>
                  <p>Or visit Google Maps, find your institute, right-click on the exact location, and select "What's here?" to see coordinates</p>
                </div>
                <div className="flex items-start">
                  <span className="font-medium text-blue-400 mr-2">3.</span>
                  <p>Copy the latitude and longitude values and paste them in the fields above</p>
                </div>
                <div className="flex items-start">
                  <span className="font-medium text-blue-400 mr-2">4.</span>
                  <p>Set an appropriate radius based on your institute's size (e.g., 100m for small institutes, 500m for large campuses)</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Organization Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Organization"
          message="This action will permanently delete your organization and ALL associated data including students, batches, workshops, fees, and users. This action cannot be undone. Are you absolutely sure you want to proceed?"
          confirmText="Delete Organization"
          cancelText="Cancel"
          danger={true}
          requireCaptcha={true}
        />
      </div>
    </div>
  );
};

export default OrganizationDetail; 